import {
  useClubAsPublic,
  useMutationSendAchVerificationEmail,
  useMutationSendSMSToMember,
  useQueryPaymentMethod,
} from "@gymflow/api";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { ModalContext, ModalWrapper, useClubSettings } from "../../providers";
import { ToastContext } from "../../providers/ToastProvider/context";
import useGymflowModels from "../../store";
import {
  Button,
  CheckIcon,
  CopyIcon,
  LayeredCircles,
  ReactiveButton,
  SMSIcon,
  Spinner,
} from "../atoms";
import CheckmarkIcon from "../atoms/icons/CheckmarkIcon";
import { EmailIcon } from "../atoms/icons/EmailIcon";
import { ShieldIcon } from "../atoms/icons/ShieldIcon";
import { XIcon } from "../atoms/icons/XIcon";

export type VerifyMicroDepositModalProps = {
  as: "staff";
  userMemberId: string;
  paymentMethodId: string;
};
export const VerifyMicroDepositModal = ({
  userMemberId,
  paymentMethodId,
}: VerifyMicroDepositModalProps) => {
  const { toast } = useContext(ToastContext);
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { popModal } = useContext(ModalContext);
  const settings = useClubSettings();

  const { data: paymentMethod, isLoading } = useQueryPaymentMethod(
    {
      api,
      memberId: userMemberId,
      paymentMethodId,
    },
    {
      refetchInterval: (query) =>
        query.state.data?.status === "PENDING" ? 5000 : false,
      refetchOnWindowFocus: true,
    },
  );
  const sendEmailMutation = useMutationSendAchVerificationEmail({ api });
  const sendSms = useMutationSendSMSToMember({ api });
  const { data: club } = useClubAsPublic({
    api,
    clubId: settings.clubId,
  });
  const scaUrl =
    api.public.userMemberApi.verifyPaymentMethodLink(paymentMethodId);

  const hideModal = () => {
    popModal();
  };
  return (
    <ModalWrapper onCancel={hideModal} className="!max-w-lg">
      <div className="flex flex-col">
        {paymentMethod?.status === "PENDING" && (
          <div className="flex flex-row justify-between">
            <LayeredCircles className="size-12" intent="secondary">
              <ShieldIcon
                className="size-5"
                pathClassName="stroke-secondary-600"
              />
            </LayeredCircles>
            <Button
              intent="transparent"
              className="z-10 aspect-square p-1"
              onClick={hideModal}
            >
              <XIcon className="size-6" pathClassName="stroke-gray-600" />
            </Button>
          </div>
        )}
        {isLoading && <Spinner className="py-4" />}

        {paymentMethod?.status === "ACTIVE" && (
          <div className="flex w-full flex-col items-center gap-4">
            <LayeredCircles className="size-14" intent="success">
              <CheckmarkIcon
                className="size-5"
                pathClassName="stroke-success-600"
              />
            </LayeredCircles>
            <div className="flex flex-col items-center">
              <div className="text-center text-xl font-bold text-gray-950">
                {t("pages.verifyMicroDepositModal.successTitle")}
              </div>
              <div className="text-center font-medium text-gray-500">
                {t("pages.verifyMicroDepositModal.successDescription")}
              </div>
            </div>
          </div>
        )}
        {(paymentMethod?.status === "INVALID" ||
          paymentMethod?.status === "REMOVED") && (
          <div className="flex w-full flex-col items-center gap-4">
            <LayeredCircles className="size-14" intent="error">
              <CheckmarkIcon
                className="size-5"
                pathClassName="stroke-error-600"
              />
            </LayeredCircles>
            <div className="flex flex-col items-center">
              <div className="text-center text-xl font-bold text-gray-950">
                {t("pages.verifyMicroDepositModal.errorTitle")}
              </div>
              <div className="text-center font-medium text-gray-500">
                {t("pages.verifyMicroDepositModal.errorDescription")}
              </div>
            </div>
          </div>
        )}
        {paymentMethod?.status === "PENDING" && (
          <div className="flex w-full flex-col gap-6">
            <div className="text-lg font-semibold">
              {t("pages.verifyMicroDepositModal.title")}
            </div>
            <div className="flex w-full flex-col items-center gap-6">
              <div className="flex w-full flex-col items-center gap-4 rounded-xl border border-gray-200 p-3">
                <div className="flex flex-col items-center gap-1">
                  <div className="font-medium">
                    {t("pages.verifyMicroDepositModal.paymentStatus")}
                  </div>
                  <div className="bg-warning-100 flex flex-row gap-1 rounded-full px-4 py-2">
                    <ShieldIcon
                      className="size-5"
                      pathClassName="stroke-warning-700"
                    />
                    <div className="text-warning-700 text-sm font-semibold">
                      {t(
                        "pages.verifyMicroDepositModal.paymentStatusAuthNeeded",
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-warning-600 text-center text-sm font-medium">
                  {t(
                    "pages.verifyMicroDepositModal.paymentStatusAuthNeededDescription",
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="font-medium">
                  {t("pages.verifyMicroDepositModal.sendAuthVia")}
                </div>
                <div className="flex flex-row gap-2">
                  <Button
                    className="flex flex-1 flex-row gap-2 text-gray-950"
                    onClick={async () => {
                      if (!paymentMethod || !club) {
                        toast({
                          message: t("portal.common.error"),
                          intent: "error",
                          type: "small",
                        });
                        return;
                      }

                      await sendSms.mutateAsync({
                        body: `Verify payment method for ${club.name}. ${scaUrl}`,
                        userMemberId: userMemberId,
                      });
                      toast({
                        message: t("pages.verifyMicroDepositModal.SMSSent"),
                        description: t(
                          "pages.verifyMicroDepositModal.SMSSentDescription",
                        ),
                      });
                    }}
                  >
                    <SMSIcon
                      className="size-5"
                      pathClassName="stroke-gray-950"
                    />
                    {t("portal.common.SMS")}
                  </Button>
                  {club?.smsEnabled && (
                    <Button
                      className="flex flex-1 flex-row gap-2 text-gray-950"
                      onClick={async () => {
                        await sendEmailMutation.mutateAsync({
                          memberId: userMemberId,
                          paymentMethodId,
                        });
                        toast({
                          message: t("pages.verifyMicroDepositModal.emailSent"),
                          description: t(
                            "pages.verifyMicroDepositModal.emailSentDescription",
                          ),
                        });
                      }}
                    >
                      <EmailIcon
                        className="size-5"
                        pathClassName="stroke-gray-950"
                      />
                      {t("portal.common.email")}
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex flex-row items-center gap-1 font-medium text-gray-500">
                <div className="mt-px h-px w-full bg-gray-300" />
                {t("portal.common.or")}
                <div className="mt-px h-px w-full bg-gray-300" />
              </div>

              <div className="flex flex-col gap-1">
                <div className="font-medium">
                  {t("pages.verifyMicroDepositModal.copyLink")}
                </div>
                <div className="flex h-11 flex-row items-center justify-between rounded-xl bg-gray-100 pl-3 pr-1">
                  <div className="line-clamp-1 font-medium text-gray-950">
                    {scaUrl}
                  </div>
                  <ReactiveButton
                    size="small"
                    className="aspect-square p-0"
                    onClick={() => {
                      navigator.clipboard.writeText(scaUrl);
                      toast({
                        message: t("portal.common.linkCopied"),
                        type: "small",
                      });
                    }}
                    reactiveContent={
                      <CheckIcon pathClassName="stroke-gray-950" />
                    }
                  >
                    <CopyIcon pathClassName="stroke-gray-950" />
                  </ReactiveButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};
